import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronUp, PhoneIncoming, PhoneOutgoing, XCircle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const CHART_COLORS = {
  answered: "bg-emerald-500",
  missed: "bg-amber-500",
  once: "bg-cyan-500",
  multi: "bg-blue-500",
  quick: "bg-rose-500",
  ring: "bg-orange-500",
  none: "bg-slate-500",
  called: "bg-indigo-500",
};

const SimpleBarChart = ({ items, isDark }) => {
  const max = Math.max(1, ...items.map((i) => i.value || 0));
  return (
    <div className="space-y-2">
      {items.map((item) => (
        <div key={item.key || item.label}>
          <div className="flex items-center justify-between text-xs mb-1">
            <span className={isDark ? "text-slate-300" : "text-gray-700"}>{item.label}</span>
            <span className={`font-semibold ${isDark ? "text-slate-200" : "text-gray-900"}`}>{item.value}</span>
          </div>
          <div className={`h-2 rounded-full overflow-hidden ${isDark ? "bg-slate-800" : "bg-gray-200"}`}>
            <div
              className={`h-full rounded-full ${CHART_COLORS[item.key] || "bg-teal-500"}`}
              style={{ width: `${Math.max(4, (item.value / max) * 100)}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const SummaryCard = ({ title, value, sub, colorClass, isDark }) => (
  <div className={`rounded-lg border p-3 ${isDark ? "border-slate-700 bg-slate-800/40" : "border-gray-200 bg-white"}`}>
    <p className={`text-[10px] uppercase tracking-wide ${isDark ? "text-slate-400" : "text-gray-500"}`}>{title}</p>
    <p className={`text-xl font-bold mt-1 ${colorClass}`}>{value ?? 0}</p>
    {sub && <p className={`text-[11px] mt-1 ${isDark ? "text-slate-400" : "text-gray-500"}`}>{sub}</p>}
  </div>
);

const DirectionBlock = ({ title, data, isDark }) => {
  if (!data) return null;
  return (
    <div className={`rounded-lg border p-3 space-y-2 ${isDark ? "border-slate-700 bg-slate-800/30" : "border-gray-200 bg-gray-50"}`}>
      <h4 className={`text-sm font-semibold ${isDark ? "text-slate-200" : "text-gray-800"}`}>{title}</h4>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div><span className={isDark ? "text-slate-400" : "text-gray-500"}>Toplam deneme</span><p className="font-semibold">{data.total_attempts}</p></div>
        <div><span className={isDark ? "text-slate-400" : "text-gray-500"}>Tekil numara</span><p className="font-semibold">{data.unique_numbers}</p></div>
        <div><span className={isDark ? "text-slate-400" : "text-gray-500"}>Cevaplanan</span><p className={`font-semibold ${isDark ? "text-emerald-400" : "text-emerald-700"}`}>{data.answered_attempts} <span className="opacity-70">({data.unique_answered} tekil)</span></p></div>
        <div><span className={isDark ? "text-slate-400" : "text-gray-500"}>Cevapsız</span><p className={`font-semibold ${isDark ? "text-amber-400" : "text-amber-700"}`}>{data.missed_attempts} <span className="opacity-70">({data.unique_missed} tekil)</span></p></div>
        <div><span className={isDark ? "text-slate-400" : "text-gray-500"}>1 kez aranan</span><p className="font-semibold">{data.called_once}</p></div>
        <div><span className={isDark ? "text-slate-400" : "text-gray-500"}>Birden fazla aranan</span><p className="font-semibold">{data.called_multiple}</p></div>
        <div><span className={isDark ? "text-slate-400" : "text-gray-500"}>Çaldır-kapat</span><p className={`font-semibold ${isDark ? "text-rose-400" : "text-rose-700"}`}>{data.quick_hangup}</p></div>
        <div><span className={isDark ? "text-slate-400" : "text-gray-500"}>Uzun çaldırma</span><p className="font-semibold">{data.ring_no_answer}</p></div>
      </div>
    </div>
  );
};

/**
 * Agent günlük çağrı analizi gövdesi (modal ve özel rapor sekmesinde ortak).
 */
const AgentDayCallAnalysisView = ({
  data,
  loading,
  isDark,
  labelClass,
  compactTableMaxHeight = "max-h-72",
  expandedTableMaxHeight = "max-h-[420px]",
  tableExpanded = false,
}) => {
  const [expandedPhone, setExpandedPhone] = useState(null);

  const summary = data?.summary;
  const byNumber = useMemo(() => data?.by_number || [], [data?.by_number]);
  const charts = data?.charts || {};

  const repeatMissed = useMemo(
    () => byNumber.filter((n) => n.pattern === "repeat_missed" || n.total_attempts > 1),
    [byNumber]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  if (!data || data.error) {
    return (
      <div className={`text-center py-12 text-sm ${labelClass}`}>
        {data?.error || "Analiz verisi yok"}
      </div>
    );
  }

  if (!summary) {
    return <div className={`text-center py-12 text-sm ${labelClass}`}>Bu gün için çağrı kaydı yok</div>;
  }

  const tableMaxH = tableExpanded ? expandedTableMaxHeight : compactTableMaxHeight;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <SummaryCard title="Toplam çağrı" value={summary.total_calls} sub={`${summary.unique_numbers} tekil numara`} colorClass={isDark ? "text-blue-400" : "text-blue-700"} isDark={isDark} />
        <SummaryCard title="Tek deneme" value={summary.single_attempt_numbers} sub="numara 1 kez arandı" colorClass={isDark ? "text-cyan-400" : "text-cyan-700"} isDark={isDark} />
        <SummaryCard title="Tekrar cevapsız" value={summary.repeat_missed_numbers} sub="aynı numaraya çoklu cevapsız" colorClass={isDark ? "text-amber-400" : "text-amber-700"} isDark={isDark} />
        <SummaryCard
          title="Geri aranan"
          value={(summary.missed_follow_up?.called_back_answered || 0) + (summary.missed_follow_up?.called_back_unanswered || 0)}
          sub={`${summary.missed_follow_up?.not_called_back || 0} geri aranmadı`}
          colorClass={isDark ? "text-emerald-400" : "text-emerald-700"}
          isDark={isDark}
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-3">
        <DirectionBlock title="Giden aramalar" data={summary.outgoing} isDark={isDark} />
        <DirectionBlock title="Gelen aramalar" data={summary.incoming} isDark={isDark} />
      </div>

      <div className="grid lg:grid-cols-3 gap-3">
        <div className={`rounded-lg border p-3 ${isDark ? "border-slate-700 bg-slate-800/30" : "border-gray-200 bg-gray-50"}`}>
          <h4 className={`text-sm font-semibold mb-3 ${isDark ? "text-slate-200" : "text-gray-800"}`}>Giden arama davranışı</h4>
          <SimpleBarChart items={charts.outgoing_patterns || []} isDark={isDark} />
        </div>
        <div className={`rounded-lg border p-3 ${isDark ? "border-slate-700 bg-slate-800/30" : "border-gray-200 bg-gray-50"}`}>
          <h4 className={`text-sm font-semibold mb-3 ${isDark ? "text-slate-200" : "text-gray-800"}`}>Cevapsız geri dönüş</h4>
          <SimpleBarChart items={charts.follow_up || []} isDark={isDark} />
        </div>
        <div className={`rounded-lg border p-3 ${isDark ? "border-slate-700 bg-slate-800/30" : "border-gray-200 bg-gray-50"}`}>
          <h4 className={`text-sm font-semibold mb-3 ${isDark ? "text-slate-200" : "text-gray-800"}`}>Numara bazlı sonuç</h4>
          <SimpleBarChart items={charts.outcome || []} isDark={isDark} />
        </div>
      </div>

      {repeatMissed.length > 0 && (
        <div className={`rounded-lg border p-3 ${isDark ? "border-amber-500/30 bg-amber-500/5" : "border-amber-200 bg-amber-50"}`}>
          <h4 className={`text-sm font-semibold mb-2 ${isDark ? "text-amber-300" : "text-amber-800"}`}>Dikkat — tekrarlayan / çoklu aramalar</h4>
          <div className="flex flex-wrap gap-2">
            {repeatMissed.slice(0, 8).map((item) => (
              <Badge key={item.phone} variant="outline" className={isDark ? "border-amber-500/40 text-amber-200" : "border-amber-300 text-amber-800"}>
                {item.phone} · {item.total_attempts} deneme · {item.missed} cevapsız
              </Badge>
            ))}
          </div>
        </div>
      )}

      {byNumber.length > 0 && (
        <div className={`rounded-lg border overflow-hidden ${isDark ? "border-slate-700" : "border-gray-200"}`}>
          <div className={`px-3 py-2 text-sm font-semibold ${isDark ? "bg-slate-800 text-slate-200" : "bg-gray-100 text-gray-800"}`}>
            Numara detayı ({byNumber.length})
          </div>
          <div className={`overflow-y-auto ${tableMaxH}`}>
            <Table className="text-xs">
              <TableHeader className={`sticky top-0 z-10 ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>
                <TableRow>
                  <TableHead>Numara</TableHead>
                  <TableHead className="text-center">Deneme</TableHead>
                  <TableHead className="text-center">Cvp / Cvsz</TableHead>
                  <TableHead>Desen</TableHead>
                  <TableHead>Geri dönüş</TableHead>
                  <TableHead className="w-8" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {byNumber.map((row) => {
                  const isExpanded = expandedPhone === row.phone;
                  return (
                    <React.Fragment key={row.phone}>
                      <TableRow className={isDark ? "border-slate-700/50" : ""}>
                        <TableCell className="font-mono">{row.phone}</TableCell>
                        <TableCell className="text-center">{row.total_attempts}</TableCell>
                        <TableCell className="text-center">
                          <span className={isDark ? "text-emerald-400" : "text-emerald-700"}>{row.answered}</span>
                          {" / "}
                          <span className={isDark ? "text-amber-400" : "text-amber-700"}>{row.missed}</span>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-[10px]">{row.pattern_label}</Badge>
                        </TableCell>
                        <TableCell className="text-[10px]">{row.follow_up_label || "—"}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setExpandedPhone(isExpanded ? null : row.phone)}>
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </Button>
                        </TableCell>
                      </TableRow>
                      {isExpanded && (
                        <TableRow className={isDark ? "border-slate-700/50 bg-slate-900/40" : "bg-gray-50"}>
                          <TableCell colSpan={6} className="p-0">
                            <div className="p-3 space-y-1">
                              {(row.attempts || []).map((attempt) => (
                                <div key={attempt.id} className={`flex flex-wrap items-center gap-2 text-[11px] rounded px-2 py-1 ${isDark ? "bg-slate-800/60" : "bg-white border border-gray-100"}`}>
                                  <span className="font-mono">{attempt.time}</span>
                                  {attempt.direction === "outgoing" ? (
                                    <PhoneOutgoing className={`w-3 h-3 ${isDark ? "text-blue-400" : "text-blue-600"}`} />
                                  ) : (
                                    <PhoneIncoming className={`w-3 h-3 ${isDark ? "text-emerald-400" : "text-emerald-600"}`} />
                                  )}
                                  {attempt.durum === "1" ? (
                                    <CheckCircle2 className={`w-3 h-3 ${isDark ? "text-emerald-400" : "text-emerald-600"}`} />
                                  ) : (
                                    <XCircle className={`w-3 h-3 ${isDark ? "text-amber-400" : "text-amber-600"}`} />
                                  )}
                                  <span>Konuşma {attempt.konusma_suresi}s</span>
                                </div>
                              ))}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentDayCallAnalysisView;
